import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AutosaveAckDto,
  AutosaveCellInput,
  CloneSubmissionInput,
  CloneSubmissionResultDto,
  DeclareSkillInput,
  DeclaredSkillDto,
  DeclaredSkillListDto,
  EditLockAckDto,
  EditLockReleaseAckDto,
  GrilleViewDto,
  PriorCampaignListDto,
  ProfileDetailDto,
  RateCollectionClient,
  RateSeniorityLevelDto,
  ReopenSubmissionAckDto,
  SkillReferentialByProfileListDto,
  SubmitAckDto,
} from '@/api/rate-collection-client';
import { useRateCollectionClient } from '@/api/rate-collection-client-context';

// =============================================================================
// use-rate-collection.ts — Hooks TanStack Query du BC rate-collection FOURNISSEUR (S12).
// =============================================================================
// Lecture (grille) en useQuery ; commandes (autosave par cellule, submit) en useMutation.
// Le client effectif vient du RateCollectionClientProvider (doublable). ZÉRO règle métier
// ici : l'autorité (session/withTenant, garde entier>0, idempotence UPSERT, inv.3 409) est
// l'API. Les hooks relaient l'intention et invalident la query grille après mutation pour
// refléter l'état serveur (lastSavedAt, submissionState).
//
// RÉSILIENCE (ST-SAISIE-02) : aucune donnée métier en localStorage. La récupération après
// coupure = RE-FETCH serveur du Brouillon persisté (invalidation/refetch de la query grille),
// JAMAIS une restauration locale.

const RATE_COLLECTION_KEY = ['rate-collection'] as const;

/** Clé de query de la grille d'une submission. */
export function grilleKey(submissionId: string): readonly unknown[] {
  return [...RATE_COLLECTION_KEY, 'grille', submissionId];
}

/** Clé de query de la liste des compétences déclarées d'une submission (S13-D). */
export function skillsKey(submissionId: string): readonly unknown[] {
  return [...RATE_COLLECTION_KEY, 'skills', submissionId];
}

/** Clé de query des campagnes antérieures participées d'une submission (S13 F1). */
export function priorCampaignsKey(submissionId: string): readonly unknown[] {
  return [...RATE_COLLECTION_KEY, 'prior-campaigns', submissionId];
}

/** Clé de query du référentiel de compétences déclarables par profil (S13 F2). */
export function skillReferentialKey(submissionId: string): readonly unknown[] {
  return [...RATE_COLLECTION_KEY, 'skill-referential', submissionId];
}

/** Clé de query de la fiche profil (SF6, pop-up de saisie). Scopée à (submission, profileCode). */
export function profileDetailKey(
  submissionId: string,
  profileCode: string,
): readonly unknown[] {
  return [...RATE_COLLECTION_KEY, 'profile-detail', submissionId, profileCode];
}

/**
 * (F1) GET campagnes ANTÉRIEURES participées (sélecteur du clone-panel). Lecture pure : aucune
 * mutation, jamais de localStorage. La liste est l'autorité de l'API (résolue sous le tenant).
 */
export function usePriorCampaigns(
  submissionId: string,
): UseQueryResult<PriorCampaignListDto, Error> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useQuery<PriorCampaignListDto, Error>({
    queryKey: priorCampaignsKey(submissionId),
    queryFn: () => client.getPriorCampaigns(submissionId),
  });
}

/**
 * (F2) GET référentiel de compétences DÉCLARABLES par profil (checkboxes du skills-panel).
 * Lecture pure : version de catalogue figée de la submission, autorité API.
 */
export function useSkillReferential(
  submissionId: string,
): UseQueryResult<SkillReferentialByProfileListDto, Error> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useQuery<SkillReferentialByProfileListDto, Error>({
    queryKey: skillReferentialKey(submissionId),
    queryFn: () => client.getSkillReferential(submissionId),
  });
}

/**
 * (SF6) GET fiche profil de LECTURE (pop-up de saisie, DBU §3.5). Chargée à la DEMANDE : la query
 * n'est ACTIVE que lorsqu'un `profileCode` est sélectionné (clic sur un profil) — `enabled` false
 * sinon, pour ne rien précharger et ne pas alourdir la grille 82×4 (ENF-01). Lecture pure.
 */
export function useProfileDetail(
  submissionId: string,
  profileCode: string | null,
): UseQueryResult<ProfileDetailDto, Error> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useQuery<ProfileDetailDto, Error>({
    queryKey: profileDetailKey(submissionId, profileCode ?? '__none__'),
    queryFn: () => client.getProfileDetail(submissionId, profileCode as string),
    enabled: profileCode !== null,
  });
}

/** (A) GET grille profil×niveau de la submission. */
export function useGrille(
  submissionId: string,
): UseQueryResult<GrilleViewDto, Error> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useQuery<GrilleViewDto, Error>({
    queryKey: grilleKey(submissionId),
    queryFn: () => client.getGrille(submissionId),
  });
}

/** Variables d'un autosave (cellule ciblée + valeur). */
export interface AutosaveCellVariables {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
  readonly input: AutosaveCellInput;
}

/**
 * (B) Autosave idempotent d'UNE cellule. N'INVALIDE PAS la grille au succès : l'ack porte le
 * `lastSavedAt`/`cellState` à jour, et un refetch global ferait clignoter la grille entière
 * (ENF-01 : édition perçue < 150 ms). L'écran applique l'ack localement (optimistic-light).
 * En cas d'erreur, l'UI affiche l'échec + retry SANS perdre la valeur saisie (mémoire applicative).
 */
export function useAutosaveCell(
  submissionId: string,
): UseMutationResult<AutosaveAckDto, Error, AutosaveCellVariables> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useMutation<AutosaveAckDto, Error, AutosaveCellVariables>({
    // networkMode 'always' (LOCAL à l'autosave, décision GATE_HUMAN John 2026-06-14) : la résilience
    // de saisie (ST-SAISIE-02) EXIGE que l'autosave TENTE le PUT même navigateur hors-ligne. Avec le
    // défaut 'online', TanStack METTRAIT EN PAUSE la mutation (état `paused`, mutationFn jamais appelé,
    // promesse jamais rejetée) ⇒ indicateur figé « en cours », le hook ne basculerait JAMAIS en `error`
    // (« échec … saisie conservée » + retry). En 'always', le fetch part et échoue VITE (offline) ou se
    // borne au timeout 3 s (rate-collection-client.http.ts) ⇒ le catch du hook passe status:'error'.
    // Porté ICI (mutation autosave), PAS sur le QueryClient global : aucun autre BC n'a ce besoin.
    networkMode: 'always',
    mutationFn: (vars) =>
      client.autosaveCell(
        submissionId,
        vars.profileCode,
        vars.seniorityLevel,
        vars.input,
      ),
  });
}

/**
 * (D) submit() : Brouillon→Soumis. Au succès, INVALIDE la grille (la submission passe Soumis ⇒
 * saisie verrouillée, l'UI re-fetch l'état figé). Le 409 (déjà Soumis) remonte tel quel (kind
 * 'already-submitted') — l'écran l'affiche proprement et re-synchronise.
 */
export function useSubmitSubmission(
  submissionId: string,
): UseMutationResult<SubmitAckDto, Error, void> {
  const client: RateCollectionClient = useRateCollectionClient();
  const queryClient = useQueryClient();
  return useMutation<SubmitAckDto, Error, void>({
    mutationFn: () => client.submit(submissionId),
    onSettled: () => {
      // Succès OU 409 : re-synchroniser sur l'état serveur (re-fetch, jamais d'état local).
      void queryClient.invalidateQueries({ queryKey: grilleKey(submissionId) });
    },
  });
}

/**
 * (A0-10) RÉ-OUVERTURE MÊME-CYCLE : une submission `Soumis` redevient `Brouillon` ÉDITABLE (pré-deadline).
 * Au SUCCÈS, INVALIDE la grille (re-fetch de l'état serveur — la grille passe éditable, jamais d'état local).
 * Le 409 `deadline-passed` (deadline dépassée) ou `not-reopenable` (état non ré-ouvrable) remonte tel quel :
 * l'écran l'affiche proprement. `networkMode:'always'` (résilience, patron S12/S13).
 */
export function useReopenSubmission(
  submissionId: string,
): UseMutationResult<ReopenSubmissionAckDto, Error, void> {
  const client: RateCollectionClient = useRateCollectionClient();
  const queryClient = useQueryClient();
  return useMutation<ReopenSubmissionAckDto, Error, void>({
    networkMode: 'always',
    mutationFn: () => client.reopenSubmission(submissionId),
    onSettled: () => {
      // Succès OU 409 : re-synchroniser sur l'état serveur (re-fetch, jamais d'état local).
      void queryClient.invalidateQueries({ queryKey: grilleKey(submissionId) });
    },
  });
}

// =============================================================================
// S13 — Clonage (A) + EditLock (B) + Déclaration de compétences (D).
// =============================================================================
// RÉSILIENCE (réutilise le patron S12) : les MUTATIONS S13 portent `networkMode:'always'`
// LOCALEMENT (jamais sur le QueryClient global — leçon S12). Sans cela, hors-ligne, TanStack
// METTRAIT EN PAUSE la mutation (état `paused`, mutationFn jamais appelé) ⇒ l'UI resterait
// figée « en cours » au lieu de basculer en `error` (échec borné par le timeout 3 s de
// rate-collection-client.http.ts). En 'always', le fetch part et échoue VITE (offline) ou se
// borne au timeout ⇒ le catch passe status:'error', l'UI signale l'échec sans perte de saisie.

/**
 * (A) Clonage par copie d'une campagne antérieure participée. Au SUCCÈS, INVALIDE la grille
 * (la cible reçoit les valeurs clonées ⇒ re-fetch du Brouillon). 409 `clone-no-prior-participation`
 * remonte tel quel ; l'écran l'affiche proprement. Warnings (profils non communs) non bloquants.
 */
export function useCloneSubmission(
  submissionId: string,
): UseMutationResult<CloneSubmissionResultDto, Error, CloneSubmissionInput> {
  const client: RateCollectionClient = useRateCollectionClient();
  const queryClient = useQueryClient();
  return useMutation<CloneSubmissionResultDto, Error, CloneSubmissionInput>({
    networkMode: 'always',
    mutationFn: (input) => client.cloneSubmission(submissionId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: grilleKey(submissionId) });
    },
  });
}

/** Variables d'une opération de verrou (cellule ciblée). */
export interface EditLockVariables {
  readonly profileCode: string;
  readonly seniorityLevel: RateSeniorityLevelDto;
}

/**
 * (B) Acquisition d'un verrou de cellule (focus). `networkMode:'always'` : la tentative part même
 * réseau dégradé (échec borné), jamais en pause silencieuse. 409 `cell-locked` ⇒ cellule en lecture
 * seule signalée (l'écran lit `kind` + `holderContactId`). N'invalide AUCUNE query (verrou = état UI).
 */
export function useAcquireLock(
  submissionId: string,
): UseMutationResult<EditLockAckDto, Error, EditLockVariables> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: (vars: EditLockVariables) =>
      client.acquireLock(submissionId, vars.profileCode, vars.seniorityLevel),
  });
}

/** (B) Libération d'un verrou (perte de focus / beforeunload). Idempotente. */
export function useReleaseLock(
  submissionId: string,
): UseMutationResult<EditLockReleaseAckDto, Error, EditLockVariables> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: (vars: EditLockVariables) =>
      client.releaseLock(submissionId, vars.profileCode, vars.seniorityLevel),
  });
}

/** (B) Heartbeat d'un verrou (repousse l'expiration tant que la cellule est focalisée). */
export function useHeartbeatLock(
  submissionId: string,
): UseMutationResult<EditLockAckDto, Error, EditLockVariables> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useMutation({
    networkMode: 'always',
    mutationFn: (vars: EditLockVariables) =>
      client.heartbeatLock(submissionId, vars.profileCode, vars.seniorityLevel),
  });
}

/** (D) GET liste des compétences déclarées d'une submission. */
export function useDeclaredSkills(
  submissionId: string,
): UseQueryResult<DeclaredSkillListDto, Error> {
  const client: RateCollectionClient = useRateCollectionClient();
  return useQuery<DeclaredSkillListDto, Error>({
    queryKey: skillsKey(submissionId),
    queryFn: () => client.listSkills(submissionId),
  });
}

/**
 * (D) Déclare (upsert idempotent) une compétence. Au succès, INVALIDE la liste des compétences
 * (re-fetch serveur, jamais d'état local). `networkMode:'always'` (résilience S13).
 */
export function useDeclareSkill(
  submissionId: string,
): UseMutationResult<DeclaredSkillDto, Error, DeclareSkillInput> {
  const client: RateCollectionClient = useRateCollectionClient();
  const queryClient = useQueryClient();
  return useMutation<DeclaredSkillDto, Error, DeclareSkillInput>({
    networkMode: 'always',
    mutationFn: (input) => client.declareSkill(submissionId, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: skillsKey(submissionId) });
    },
  });
}

/** Variables d'un retrait de compétence (profil + skill). */
export interface RemoveSkillVariables {
  readonly profileCode: string;
  readonly skillCode: string;
}

/** (D) Retire une compétence déclarée. Au succès, INVALIDE la liste. `networkMode:'always'`. */
export function useRemoveSkill(
  submissionId: string,
): UseMutationResult<{ readonly removed: boolean }, Error, RemoveSkillVariables> {
  const client: RateCollectionClient = useRateCollectionClient();
  const queryClient = useQueryClient();
  return useMutation<{ readonly removed: boolean }, Error, RemoveSkillVariables>({
    networkMode: 'always',
    mutationFn: (vars) => client.removeSkill(submissionId, vars.profileCode, vars.skillCode),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: skillsKey(submissionId) });
    },
  });
}
